import { type FunctionComponent, type ReactElement, useState } from 'react'
import { useLanguage } from '@/context/LanguageContext'
import { useTheme } from '@/context/ThemeContext'
import useIntl from '@/utils/intl'
import { useNetworkCoverage } from '@/lib/network-coverage-v2/useNetworkCoverage'
import TabBar from '@/components/page/network-coverage-v2/TabBar'
import MapSection from '@/components/page/network-coverage-v2/MapSection'
import NetworkCoverageTab from '@/components/page/network-coverage-v2/tabs/NetworkCoverageTab'
import HomeBroadbandTab from '@/components/page/network-coverage-v2/tabs/HomeBroadbandTab'
import WifiTab from '@/components/page/network-coverage-v2/tabs/WifiTab'
import TravelPointsTab from '@/components/page/network-coverage-v2/tabs/TravelPointsTab'
import { TAB_ID } from '@/lib/network-coverage-v2/networkCoverageConstants'
import { getDistance } from '@/lib/network-coverage-v2/helpers'
import * as ga from '@/utils/ga'

const NetworkCoveragePage: FunctionComponent = () => {
  const { locale } = useLanguage()
  const { theme } = useTheme()
  const { translate } = useIntl()
  const iconColor = theme === 'dark' ? 'white' : 'black'
  const store = useNetworkCoverage(locale)
  const [locationError, setLocationError] = useState<string | null>(null)

  const handleTabChange = (id: number): void => { store.setActiveTab(id) }
  const handleMapStateChange = (partial: Parameters<typeof store.setMapState>[0]): void => { store.setMapState(partial) }
  const handleHbbModalOpenChange = (open: boolean): void => { store.setIsHbbModalOpen(open) }
  const handleNetworkModalOpenChange = (open: boolean): void => { store.setIsNetworkModalOpen(open) }
  const handleUserLocationChange = (location: { lat: number, lng: number } | null): void => { store.setUserLocation(location) }

  const handleLocationRequest = (): void => {
    ga.event('network_coverage_location_click')
    if (!window.isSecureContext) {
      setLocationError(translate('location.error.httpsRequired'))
      return
    }
    if (!('geolocation' in navigator)) {
      setLocationError(translate('location.error.unsupported'))
      return
    }
    setLocationError(null)
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocationError(null)
        const lat = pos.coords.latitude
        const lng = pos.coords.longitude
        handleUserLocationChange({ lat, lng })

        if (store.activeTab === TAB_ID.NETWORK && store.cities.length > 0) {
          let closestCity = store.cities[0]
          let minDistance = Infinity
          store.cities.forEach(city => {
            const d = getDistance(lat, lng, parseFloat(city.latitude), parseFloat(city.longitude))
            if (d < minDistance) {
              minDistance = d
              closestCity = city
            }
          })
          if (closestCity !== undefined) {
            store.handleCitySelect(closestCity.zipcode, { updateMapCenter: false })
          }
        }

        handleMapStateChange({ center: { lat, lng }, zoom: 15 })
      },
      (err) => {
        console.error('Error getting location:', err)
        let detailedError = translate('location.error.unavailable')
        if (err.code === 1) {
          detailedError = translate('location.error.permissionDenied')
        } else if (err.code === 2) {
          detailedError = translate('location.error.positionUnavailable')
        } else if (err.code === 3) {
          detailedError = translate('location.error.timeout')
        }
        setLocationError(detailedError)
      },
      { enableHighAccuracy: false, timeout: 30000, maximumAge: 60000 }
    )
  }

  const renderTabContent = (): ReactElement | null => {
    switch (store.activeTab) {
      case TAB_ID.NETWORK:
        return (
          <NetworkCoverageTab
            mapState={store.mapState}
            onMapStateChange={handleMapStateChange}
            selectedCity={store.selectedCity}
            selectedDistrict={store.selectedDistrict}
            quarters={store.quarters}
            cityItems={store.ncCityItems}
            districtItems={store.ncDistrictItems}
            iconColor={iconColor}
            onCitySelect={store.handleCitySelect}
            onDistrictSelect={store.handleDistrictSelect}
            onNetworkTypeSelect={store.handleNetworkTypeSelect}
            onUserLocationChange={handleUserLocationChange}
          />
        )
      case TAB_ID.HBB:
        return (
          <HomeBroadbandTab
            selectedCity={store.selectedCity}
            selectedDistrict={store.selectedDistrict}
            selectedQuarter={store.selectedQuarter}
            selectedBuilding={store.selectedBuilding}
            quarters={store.quarters}
            buildings={store.buildings}
            cityItems={store.cityItems}
            districtItems={store.districtItems}
            quarterItems={store.quarterItems}
            buildingItems={store.buildingItems}
            iconColor={iconColor}
            isHbbModalOpen={store.isHbbModalOpen}
            isNetworkModalOpen={store.isNetworkModalOpen}
            onHbbModalOpenChange={handleHbbModalOpenChange}
            onNetworkModalOpenChange={handleNetworkModalOpenChange}
            onCitySelect={(zip) => { store.handleCitySelect(zip) }}
            onDistrictSelect={(zip) => { store.handleDistrictSelect(zip) }}
            onQuarterSelect={store.handleQuarterSelect}
            onBuildingSelect={store.handleBuildingSelect}
          />
        )
      case TAB_ID.WIFI:
        return (
          <WifiTab
            selectedCity={store.selectedCity}
            selectedDistrict={store.selectedDistrict}
            districts={store.districts}
            cityItems={store.cityItems}
            districtItems={store.districtItems}
            iconColor={iconColor}
            onCitySelect={(zip) => { store.handleCitySelect(zip) }}
            onDistrictSelect={(zip) => { store.handleDistrictSelect(zip) }}
          />
        )
      case TAB_ID.TRAVEL:
        return (
          <TravelPointsTab
            cities={store.cities}
            selectedCity={store.selectedCity}
            travelPoints={store.travelPoints}
            selectedTravelPoint={store.selectedTravelPoint}
            cityItems={store.cityItems}
            iconColor={iconColor}
            onTravelPointSelect={store.handleTravelPointSelect}
            onCityChange={(city, query) => {
              store.setSelectedCity(city)
              void store.fetchTravelPoint(query)
            }}
          />
        )
      default:
        return null
    }
  }

  return (
    <div className='w-full flex flex-col items-center'>
      <div className='flex lg:flex-row flex-col rounded-xl bg-surfaceInverse w-[95vw] 2xl:h-[80vh] h-full'>
        <div className='flex flex-col 2xl:w-[30vw] w-full border-r border-secondaryMidStroke'>
          <div className='p-6 border-b border-secondaryMidStroke'>
            <TabBar activeTab={store.activeTab} onTabChange={handleTabChange} />
          </div>
          {renderTabContent()}
          <div className='mt-auto p-4 border-t border-secondaryMidStroke'>
            <button
              type='button'
              onClick={handleLocationRequest}
              className={`w-full py-2 mb-4 border border-secondaryMidStroke shadow-sm rounded-lg transition-colors flex items-center justify-center gap-2 cursor-pointer ${
                theme === 'dark' ? 'bg-surfaceLow hover:opacity-80 text-white' : 'bg-white hover:bg-gray-50 text-textHigh'
              }`}
            >
              <svg xmlns='http://www.w3.org/2000/svg' height='20px' viewBox='0 0 24 24' width='20px' fill='#4436fa'>
                <path d='M0 0h24v24H0V0z' fill='none' />
                <path d='M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3c-.46-4.17-3.77-7.48-7.94-7.94V1h-2v2.06C6.83 3.52 3.52 6.83 3.06 11H1v2h2.06c.46 4.17 3.77 7.48 7.94 7.94V23h2v-2.06c4.17-.46 7.48-3.77 7.94-7.94H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z' />
              </svg>
              <span className='text-sm font-medium text-textHigh'>{translate('seeLocation')}</span>
            </button>
            {locationError !== null && (
              <p className='text-red-500 text-xs mb-2'>{locationError}</p>
            )}
            <p className='text-base font-bold mb-2 text-textHigh'>{translate('legend.legend')}</p>
            {store.activeTab === TAB_ID.WIFI
              ? (
                <div className='flex flex-col items-start gap-1'>
                  <p className='text-base font-bold text-textHigh'>{translate('legend.wifi')}</p>
                  <svg width='28' height='32' viewBox='0 0 28 32' fill='none' xmlns='http://www.w3.org/2000/svg'>
                    <path
                      d='M9.6 7.68311e-07C6.23969 4.99377e-07 4.55953 3.6491e-07 3.27606 0.653961C2.14709 1.2292 1.2292 2.14708 0.653963 3.27606C1.89754e-06 4.55953 1.76153e-06 6.23969 1.48951e-06 9.6L7.77136e-07 18.4C5.05113e-07 21.7603 3.69101e-07 23.4405 0.653961 24.7239C1.2292 25.8529 2.14708 26.7708 3.27606 27.346C4.55953 28 6.23969 28 9.6 28H18.4C21.7603 28 23.4405 28 24.7239 27.346C25.8529 26.7708 26.7708 25.8529 27.346 24.7239C28 23.4405 28 21.7603 28 18.4V9.6C28 6.23969 28 4.55953 27.346 3.27606C26.7708 2.14709 25.8529 1.2292 24.7239 0.653963C23.4405 1.876e-06 21.7603 1.74153e-06 18.4 1.4726e-06L9.6 7.68311e-07Z'
                      fill='#4436fa'
                    />
                    <path
                      d='M6.00153 11.7173C7.98522 9.61011 10.8346 8.28906 14.0008 8.28906C17.1669 8.28906 20.0163 9.61011 22 11.7173M19.7145 14.0028C18.2222 12.5857 16.212 11.7173 14.0008 11.7173C11.7895 11.7173 9.77928 12.5857 8.28702 14.0028M16.8576 16.2883C15.9347 15.5757 15.3089 15.1455 14.0008 15.1455C12.6926 15.1455 12.0669 15.5757 11.1439 16.2883M14.5721 19.1452C14.5721 19.4607 14.3163 19.7165 14.0008 19.7165C13.6852 19.7165 13.4294 19.4607 13.4294 19.1452M14.5721 19.1452C14.5721 18.8296 14.3163 18.5738 14.0008 18.5738C13.6852 18.5738 13.4294 18.8296 13.4294 19.1452M14.5721 19.1452H13.4294'
                      stroke='white'
                      strokeWidth='1.3162'
                      strokeLinecap='round'
                      strokeLinejoin='round'
                    />
                    <path d='M14 32L9.66987 27.5L18.3301 27.5L14 32Z' fill='#4436fa' />
                  </svg>
                </div>
                )
              : (
                <div className='flex flex-col items-start gap-1'>
                  <p className='text-base font-bold text-textHigh'>{translate('legend.quality')}</p>
                  <div className='flex flex-row items-center gap-3'>
                    <p className='text-sm font-medium w-14 truncate text-textHigh'>{translate('legend.good')}</p>
                    <svg width='30' height='12' viewBox='0 0 30 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <rect width='30' height='12' rx='6' fill='#fc3939' />
                    </svg>
                  </div>
                  <div className='flex flex-row items-center gap-3'>
                    <p className='text-sm font-medium w-14 truncate text-textHigh'>{translate('legend.medium')}</p>
                    <svg width='30' height='12' viewBox='0 0 30 12' fill='none' xmlns='http://www.w3.org/2000/svg'>
                      <rect width='30' height='12' rx='6' fill='#4b38f7' />
                    </svg>
                  </div>
                </div>
                )}
          </div>
        </div>
        <MapSection
          activeTab={store.activeTab}
          mapState={store.mapState}
          userLocation={store.userLocation}
        />
      </div>
    </div>
  )
}

export default NetworkCoveragePage
